import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "../lib/api.js";
import type { EngagementRef } from "./useEngagements.js";
import type { Quadrant } from "@msc/shared";

// ── DTOs ────────────────────────────────────────────────────────────────────

export interface SegmentRef {
  id: string;
  name: string;
}

export interface EmployeeRef {
  id: string;
  fullName: string;
  email: string;
}

export interface SegmentLeadRef {
  employeeId: string;
  fullName: string;
}

export interface CustomerSegmentRef {
  id: string;
  name: string;
  /** Abgeleitete Leads der Beauftragungen — mehrere pro Zelle möglich. */
  leads: SegmentLeadRef[];
  /** BCG-Quadrant der Zelle (rollierende 12M) — nur in der Liste geliefert. */
  quadrant?: Quadrant | null;
  /** Für alle ausgeblendet — kein Servicekunde in diesem Bereich. */
  ausgeblendet?: boolean;
  ausgeblendetGrund?: string | null;
  activeProjectCount?: number;
  expectedRevenueEur?: string;
}

export interface CustomerListItem {
  id: string;
  name: string;
  salesforceAccountId: string;
  active: boolean;
  /** Persönlicher Stern — Favoriten stehen in der Liste oben. */
  favorit?: boolean;
  segments: CustomerSegmentRef[];
  /** BCG quadrant in the active period (per dominant segment) */
  bcgQuadrant: Quadrant | null;
  /** Wird in Phase 1.x ergänzt — derzeit nicht von der API geliefert. */
  lastOnsiteVisitDate?: string | null;
}

export interface CustomersPage {
  items: CustomerListItem[];
  total: number;
  page: number;
  pageSize: number;
}

export interface ServiceProject {
  id: string;
  salesforceProjectId: string;
  projectNumber: string;
  title: string;
  segmentId: string;
  status: string;
  /** Name des Projektleiters laut Salesforce (null, wenn in SF nicht gesetzt). */
  projectLeadSfName: string | null;
  /** Zugehörige Servicebeauftragung (null = unzugeordnet, BL-Review). */
  engagementId: string | null;
  engagementLinkSource: "auto" | "manual" | null;
  startDate: string;
  endDate: string;
  assignments: unknown[];
}

export interface CustomerComponent {
  id: string;
  name: string;
  category: string | null;
  status: "produktiv" | "in_einfuehrung" | "abgekuendigt";
}

/** Monthly revenue row as returned by the API. */
export interface RevenueDataPoint {
  segmentId: string;
  segmentName: string;
  /** First day of month, ISO date */
  yearMonth: string;
  /** Decimal string ("60000.00") */
  amountEur: string;
}

export interface OnsiteVisit {
  id: string;
  scheduledAt: string;
  status: "planned" | "done" | "postponed" | "cancelled" | "clarifying";
  shortMinutes: string | null;
  /** Pflicht ab T11: Termin pro (Kunde × Segment). */
  segmentId: string;
}

export interface CampaignParticipation {
  id: string;
  campaignId: string;
  campaignTitle: string;
  campaignStatus: string;
  relevant: "not_assessed" | "yes" | "no";
  presented: "not_done" | "planned" | "yes" | "no";
  installed: "not_done" | "in_progress" | "yes" | "no";
  notes: string | null;
}

export interface CustomerNote {
  id: string;
  note: string;
  /** Pflicht-FK auf das Segment, zu dem die Notiz fachlich gehört (T12). */
  segmentId: string;
  /** Wenn true: auch für PLs anderer Segmente desselben Kunden sichtbar. */
  crossSegment: boolean;
  /** Verfasser — entscheidet mit der Admin-Rolle über Ändern und Löschen. */
  authorId: string;
  authorName?: string;
  /** Gesetzt, sobald die Notiz einmal geändert wurde — sonst null. */
  updatedAt?: string | null;
  createdAt: string;
}

export interface CustomerDetail {
  id: string;
  name: string;
  salesforceAccountId: string;
  active: boolean;
  segments: CustomerSegmentRef[];
  /** Servicebeauftragungen (alle Segmente; UI gruppiert per segmentId). */
  engagements: EngagementRef[];
  serviceProjects: ServiceProject[];
  components: CustomerComponent[];
  revenues: RevenueDataPoint[];
  recentVisits: OnsiteVisit[];
  nextPlannedVisit: OnsiteVisit | null;
  activeCampaigns: CampaignParticipation[];
  notes: CustomerNote[];
}

// ── Query Hooks ──────────────────────────────────────────────────────────────

export interface UseCustomersOpts {
  segmentId?: string[];
  q?: string;
  /** Alphabet-Filter: Anfangsbuchstabe des Namens. */
  letter?: string;
  page: number;
  /** Nur Kunden, die mindestens ein Segment ohne PL haben (BL-Sicht). */
  withoutLead?: boolean;
  /** BL-Review: nur Kunden mit unzugeordneten Projekten. */
  withUnassignedProjects?: boolean;
  /**
   * Auch Abgänge zeigen — Kunden ohne laufendes Service-Projekt. Standardmäßig
   * aus: Wer nach „seinen Kunden" sucht, meint die laufenden. Ohne den Schalter
   * wären sie nirgends erreichbar, obwohl ihr Umsatz in den Jahreszahlen steht.
   */
  includeInactive?: boolean;
  /** Nur die eigenen Favoriten. */
  nurFavoriten?: boolean;
  /**
   * Auch ausgeblendete Kunden zeigen — der Gegenschalter zum Ausblenden.
   * Nichts soll unerreichbar sein, nur aus dem Weg.
   */
  includeHidden?: boolean;
}

export function useCustomers(opts: UseCustomersOpts) {
  const params = new URLSearchParams();
  if (opts.segmentId && opts.segmentId.length > 0) {
    for (const s of opts.segmentId) {
      params.append("segmentId", s);
    }
  }
  if (opts.q) params.set("q", opts.q);
  if (opts.letter) params.set("letter", opts.letter);
  if (opts.withoutLead) params.set("withoutLead", "true");
  if (opts.withUnassignedProjects) params.set("withUnassignedProjects", "true");
  if (opts.includeInactive) params.set("includeInactive", "true");
  if (opts.nurFavoriten) params.set("nurFavoriten", "true");
  if (opts.includeHidden) params.set("includeHidden", "true");
  params.set("page", String(opts.page));

  const qs = params.toString();
  const path = `/customers${qs ? `?${qs}` : ""}`;

  return useQuery<CustomersPage>({
    queryKey: ["customers", opts],
    queryFn: () => api.get<CustomersPage>(path),
  });
}

export function useCustomer(id: string) {
  return useQuery<CustomerDetail>({
    queryKey: ["customers", id],
    queryFn: () => api.get<CustomerDetail>(`/customers/${id}`),
    enabled: Boolean(id),
  });
}

// ── Mutation Hooks ───────────────────────────────────────────────────────────

interface AddNotePayload {
  note: string;
  segmentId: string;
  crossSegment: boolean;
}

export function useAddNote(customerId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: AddNotePayload) =>
      api.post<CustomerNote>(`/customers/${customerId}/notes`, payload),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["customers", customerId] });
    },
  });
}

/**
 * Ändern und Löschen einer Notiz. Beides dürfen nur der Verfasser und Admins;
 * die Grenze zieht der Server (`NOTE_NOT_OWN`, 403) — die Oberfläche blendet
 * die Knöpfe nur zusätzlich aus, damit niemand ins Leere klickt.
 */
export function useUpdateNote(customerId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ noteId, note }: { noteId: string; note: string }) =>
      api.patch<{ ok: true }>(`/customers/${customerId}/notes/${noteId}`, { note }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["customers", customerId] });
    },
  });
}

export function useDeleteNote(customerId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (noteId: string) =>
      api.delete<{ ok: true }>(`/customers/${customerId}/notes/${noteId}`),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["customers", customerId] });
    },
  });
}

// ── Favorit und Ausblenden ──────────────────────────────────────────────────

/**
 * Warum hier pauschal alles verworfen wird.
 *
 * Favorit und Ausblendung wirken NICHT nur in der Kundenliste: Sie bestimmen
 * Reihenfolge und Menge in Weekly-Zeilen, BCG-Matrix, Startseite, Terminen,
 * Wartung und der Statusbericht-Auswahl. Die Reichweite der geschriebenen
 * Daten ist also „praktisch jede Liste", und der Schlüssel muss die Reichweite
 * abbilden (CLAUDE.md, „Der Schlüssel im Zwischenspeicher muss die Reichweite
 * der Daten abbilden"). Eine Aufzählung einzelner Schlüssel wäre beim nächsten
 * neuen Modul still unvollständig — und ein Stern, der in der Wartungsliste
 * eine halbe Minute nicht wirkt, sieht aus wie ein Fehler des Servers.
 *
 * Der Preis ist ein Nachladen der offenen Ansichten. Beides sind seltene
 * Klicks, keine Tippeingaben.
 */
function verwirfAlleListen(queryClient: ReturnType<typeof useQueryClient>): void {
  void queryClient.invalidateQueries();
}

/** Stern setzen oder wegnehmen — immer für den angemeldeten Nutzer. */
export function useSetFavorit() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ customerId, favorit }: { customerId: string; favorit: boolean }) =>
      api.put<{ ok: true }>(`/customers/${customerId}/favorit`, { favorit }),
    onSuccess: () => verwirfAlleListen(queryClient),
  });
}

/** Eine (Kunde × Bereich)-Zelle für alle aus- oder wieder einblenden. */
export function useSetAusgeblendet() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      customerId,
      segmentId,
      ausgeblendet,
      grund,
    }: {
      customerId: string;
      segmentId: string;
      ausgeblendet: boolean;
      grund?: string;
    }) =>
      api.put<{ ok: true }>(
        `/customers/${customerId}/segments/${segmentId}/ausgeblendet`,
        { ausgeblendet, ...(grund ? { grund } : {}) },
      ),
    onSuccess: () => verwirfAlleListen(queryClient),
  });
}

export interface AusgeblendeteZelle {
  customerId: string;
  customerName: string;
  segmentId: string;
  segmentName: string;
  grund: string | null;
  ausgeblendetVon: string | null;
  ausgeblendetAm: string;
}

/** Die Pflegeliste: alle ausgeblendeten Zellen der eigenen Reichweite. */
export function useAusgeblendete(enabled = true) {
  return useQuery<{ items: AusgeblendeteZelle[] }>({
    queryKey: ["customers", "ausgeblendet"],
    queryFn: () => api.get<{ items: AusgeblendeteZelle[] }>("/customers/ausgeblendet"),
    enabled,
  });
}
